import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SkipSelf,
  ViewChild
} from '@angular/core';
import {AbstractControl, ControlContainer, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subscription} from 'rxjs/Subscription';
import {coerceBooleanProperty, coerceNumberProperty} from '@angular/cdk/coercion';
import * as moment from 'moment';
import {MdDatepicker} from '@angular/material';

@Component({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FormDatepickerComponent,
    }
  ],
  selector: 'app-form-datepicker',
  templateUrl: './form-datepicker.component.html',
  styleUrls: ['./form-datepicker.component.scss', '../form-components-styles.scss']
})
export class FormDatepickerComponent implements OnInit, ControlValueAccessor, OnDestroy, DoCheck {

  /**
   * Datepicker generico. Para escribir valores desde el formulario se recomienda usar
   * el modulo moment con el metodo toDate(). Ej.:
   *
   * FormBuilder.group({
   *       date: [moment().toDate(), []],
   *     });
   *
   * El valor que se retornará al seleccionar una fecha posee el mismo formato.
   * Para inicializar se puede usar un string vacío. Ej.: date: ['', []],
   * */

  // Label del Datepicker
  @Input() label = '';

  // Mantener el espacio del label, desactivar para casos donde no se requiere label
  // (mantener en formularios para evitar conflictos de tamaño de inputs)
  _keepLabelSpace = true;
  @Input() set keepLabelSpace(val: boolean) {
    this._keepLabelSpace = coerceBooleanProperty(val);
  }

  // Placeholder del Datepicker
  @Input() placeholder = '';

  // Mensaje de error Inferior (solo acepta uno, podria prepararse una property para un array y mostrar en vertical)
  @Input() errorMessage = '';

  // Mantener el espacio del error, desactivar si no hay validaciones
  // (mantener en formularios para evitar conflictos de tamaño de inputs)
  _keepErrorSpace = true;
  @Input() set keepErrorSpace(val: boolean) {
    this._keepErrorSpace = coerceBooleanProperty(val);
  }

  // Icono de google (fonts.google.com/icons)
  @Input() googleIconName = '';

  // Icono SVG local
  @Input() svgIconName = '';

  // Mostrar el boton X para eliminar
  _allowClearButton = false;
  @Input() set allowClearButton(val: boolean) {
    this._allowClearButton = coerceBooleanProperty(val);
  }

  _minDate: Date = moment('01/01/1970', 'DD/MM/YYYY').toDate();
  // Fecha minima. Usar en string el fotmato DD/MM/YYYY
  @Input() set minDate(val: string) {
    this._minDate = moment(val, 'DD/MM/YYYY').toDate();
  }

  _maxDate: Date = moment('01/01/2500', 'DD/MM/YYYY').toDate();
  // Fecha maxima. Usar en string el fotmato DD/MM/YYYY
  @Input() set maxDate(val: string) {
    this._maxDate = moment(val, 'DD/MM/YYYY').toDate();
  }

  // Mostrar un spinner en lugar del icono (se recomienda deshabilitar el componente)
  _loading = false;
  @Input() set loading(val: boolean) {
    this._loading = coerceBooleanProperty(val);
  }

  // Habilitar la UI touch
  _touchUI = false;
  @Input() set touchUI(val: boolean) {
    this._touchUI = coerceBooleanProperty(val);
  }

  // Valor del Datepicker
  value: any = null;

  // Flag Touched
  touched = false;

  // Flag Disabled
  disabled = false;

  // Flag Control Valido
  validStatus = false;

  // Hacky, levantamos el control usando el mismo nombre de property que se usa para acceder al form
  @Input() formControlName: string;
  // Control del Form
  private control: AbstractControl;
  // Subscripcion al estado Valid
  private statusSubscription: Subscription = new Subscription();

  // Atributos de Form
  // Callback al cambiar el valor del Datepicker
  onChange = (val) => {};

  // Callback al cambiar a estado Touched
  onTouched = () => {};

  // En el constructor obtenemos el formGroup al cual pertenece el Datepicker
  constructor(
    @Optional() @Host() @SkipSelf()
    private controlContainer: ControlContainer
  ) { }

  ngOnInit() {
    this.initializeFormControl();
  }

  ngOnDestroy() {
    this.statusSubscription.unsubscribe();
  }

  ngDoCheck() {
    if (this.control && this.control.touched && !this.touched) {
      this.markAsTouched();
    }
  }

  initializeFormControl() {
    if (!this.controlContainer) {
      console.warn('Can\'t find parent FormGroup directive');
      return;
    }

    if (!this.formControlName) {
      console.warn('Missing FormControlName directive from host element of the component');
      return;
    }

    this.control = this.controlContainer.control.get(this.formControlName);
    this.validStatus = this.control.valid;
    this.statusSubscription = this.control.statusChanges.subscribe(x => {
      this.validStatus = !this.control.invalid; // usamos invalid pues valid posee un bug
    });
  }

  // cuando hay un cambio en elDatepicker, llamamos a la callbackFunction onchange para que lo registre el form
  dateChanged() {
    // usamos un timeout pues el valor del binding aun no cambió
    window.setTimeout(() => {
      this.onChange(this.value);
    });
  }

  // Eliminar el contenido del Datepicker
  clearValue() {
    if (this.disabled) {
      return;
    }
    // esto va a generar un error en la consola obligatoriamente en la consola dado que
    // datepicker no acepta valores vacios ni null. No queda otra que ignorar el error
    this.value = null;
    this.dateChanged();
  }

  parseDate(value: any) {
    return moment(value).format('DD/MM/YY');
  }

  openDatepicker(datepicker: MdDatepicker<any>) {
    if (this.disabled) {
      return;
    }
    datepicker.open();
  }

  /** En caso de que se quiera modificar el valor por codigo
   * mediante AbstractControl.setValue(value) se llamará a este método
   * */
  writeValue(val: string) {
    this.value = val;
  }

  /** El formulario registrara esta callbackFunction para notificar cuando hubo un
   * cambio en el formulario del lado del usuario
   * */
  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  /** El formulario registrara esta callbackFunction para notificar el estado Touched
   * */
  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  /** El formulario podra usar esta funcion para deshabilitar el control
   * */
  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  // Marcamos el control como Touched
  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/common/form-components/form-datepicker/form-datepicker.component.ts